import { useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import type { WorkbookInstance } from '@fortune-sheet/react';
import { getInternals, update as coreUpdate, recomputeDisplayValue, type WorkbookInternals } from './workbookInternals';
import { isSafeNumFmt, buildNumFmt, EXCEL_NUMBER_PRESETS, RUPEE_SYMBOL, type NumFmtNegativeStyle } from './numberFormats';
import { usedContentDims, type SelectionRange } from './navigation';

export type FormatCellsCategory =
    | 'General'
    | 'Number'
    | 'Currency'
    | 'Accounting'
    | 'Percentage'
    | 'Scientific'
    | 'Date'
    | 'Time'
    | 'Text'
    | 'Custom';

const DATE_PRESETS = ['yyyy-MM-dd', 'dd-mmm-yy', 'dd-mm-yyyy', 'mm/dd/yyyy'];
const TIME_PRESETS = ['h:mm AM/PM', 'h:mm:ss AM/PM', 'h:mm'];

const PREVIEW_VALUE = 1234.5;

/** Applies `fa` to every cell in `ranges` — one `setContextWithProduce`
 * (patch present) or one `batchCallApis` (public fallback), either way ONE
 * undo step. `t` is forced to `'s'` for the Text format (`@`); otherwise
 * each cell keeps its own existing type (never forces a string cell to `'n'`). */
export const applyNumberFormatToSelection = (
    wb: WorkbookInstance,
    internals: WorkbookInternals | null,
    ranges: SelectionRange[],
    fa: string,
): void => {
    if (internals) {
        // `fa` is invariant across the whole selection, so the same string
        // value re-formats to the same `m` every time — memoize per unique
        // value instead of re-parsing `fa` through SSF for every cell.
        const mCache = new Map<string, string | undefined>();
        const getM = (v: NonNullable<unknown>): string | undefined => {
            const key = String(v);
            if (!mCache.has(key)) mCache.set(key, recomputeDisplayValue(fa, v));
            return mCache.get(key);
        };

        internals.setContextWithProduce((d) => {
            const sheet = d.luckysheetfile.find((s) => s.id === d.currentSheetId);
            if (!sheet) return;
            if (!sheet.data) sheet.data = [];
            // A blank cell WITHIN the sheet's REAL used extent (content only
            // — see usedContentDims) still gets
            // formatted — Excel's own "format a column then type" behaviour
            // — but a blank beyond it is skipped entirely (never even given
            // a row array), so a whole-column/whole-sheet selection (which
            // reaches far past the extent) stays cheap. liveDims (the
            // PADDED grid, ≥50 rows) formatted every blank instead and could
            // move Ctrl+End's own target to the padding — usedContentDims
            // never grows just from applying a format, so it can't creep.
            const dims = usedContentDims(sheet);
            ranges.forEach((range) => {
                for (let r = range.row[0]; r <= range.row[1] && r < dims.rows; r += 1) {
                    if (!sheet.data![r]) sheet.data![r] = [];
                    for (let c = range.column[0]; c <= range.column[1]; c += 1) {
                        const cell = sheet.data![r][c];
                        if (cell == null && c >= dims.cols) continue;
                        const t = fa === '@' ? 's' : cell?.ct?.t ?? 'n';
                        const next = { ...cell, ct: { ...cell?.ct, fa, t } };
                        // The public setCellFormat this patch path stands in for also
                        // recomputes the cached display string `m` (core:76331) — without
                        // this the format is stored but the grid keeps showing the old
                        // text. `v` itself is never coerced (a typed-string number stays a
                        // string; that storage shape is a pre-existing, out-of-scope
                        // follow-up), only the value fed to the formatter is.
                        if (cell?.v != null) {
                            const m = getM(cell.v);
                            if (m !== undefined) next.m = m;
                        }
                        sheet.data![r][c] = next;
                    }
                }
            });
        });
        return;
    }

    const calls: { name: string; args: unknown[] }[] = [];
    ranges.forEach((range) => {
        const cells = wb.getCellsByRange({ row: range.row, column: range.column });
        cells.forEach((row, i) => {
            row.forEach((cell, j) => {
                const t = fa === '@' ? 's' : cell?.ct?.t ?? 'n';
                calls.push({ name: 'setCellFormat', args: [range.row[0] + i, range.column[0] + j, 'ct', { fa, t }] });
            });
        });
    });
    wb.batchCallApis(calls);
};

export interface UseFormatCellsDialogArgs {
    open: boolean;
    workbookRef: RefObject<WorkbookInstance>;
    onClose: () => void;
}

export interface UseFormatCellsDialogReturn {
    category: FormatCellsCategory;
    setCategory: (c: FormatCellsCategory) => void;
    decimals: number;
    setDecimals: (n: number) => void;
    thousands: boolean;
    setThousands: (b: boolean) => void;
    negative: NumFmtNegativeStyle;
    setNegative: (n: NumFmtNegativeStyle) => void;
    dateTimeFmt: string;
    setDateTimeFmt: (s: string) => void;
    custom: string;
    setCustom: (s: string) => void;
    datePresets: readonly string[];
    timePresets: readonly string[];
    fa: string;
    valid: boolean;
    preview: string;
    handleApply: () => void;
}

/**
 * Owns Format Cells' category/decimals/… state, the `fa`/`valid`/`preview`
 * derivation, and Apply. `FormatCellsDialog.tsx` is presentational.
 */
export const useFormatCellsDialog = ({ open, workbookRef, onClose }: UseFormatCellsDialogArgs): UseFormatCellsDialogReturn => {
    const [category, setCategoryRaw] = useState<FormatCellsCategory>('Number');
    const [decimals, setDecimals] = useState(2);
    const [thousands, setThousands] = useState(true);
    const [negative, setNegative] = useState<NumFmtNegativeStyle>('minus');
    const [dateTimeFmt, setDateTimeFmt] = useState(DATE_PRESETS[0]);
    const [custom, setCustom] = useState('');

    // Date and Time share one `dateTimeFmt` state — switching category must
    // reset it to the NEW category's own first preset, or Time silently
    // applies whatever Date preset (or vice versa) was last selected.
    const setCategory = (next: FormatCellsCategory): void => {
        setCategoryRaw(next);
        if (next === 'Date') setDateTimeFmt(DATE_PRESETS[0]);
        else if (next === 'Time') setDateTimeFmt(TIME_PRESETS[0]);
    };

    useEffect(() => {
        if (open) {
            setCategory('Number');
            setDecimals(2);
            setThousands(true);
            setNegative('minus');
            setDateTimeFmt(DATE_PRESETS[0]);
            setCustom('');
        }
    }, [open]);

    const presets = useMemo(() => EXCEL_NUMBER_PRESETS(RUPEE_SYMBOL), []);

    const fa = useMemo(() => {
        switch (category) {
            case 'General':
                return 'General';
            case 'Number':
                return buildNumFmt('number', { decimals, thousands, negative });
            case 'Currency':
                return buildNumFmt('currency', { decimals, currency: RUPEE_SYMBOL, negative });
            case 'Accounting':
                return presets.Accounting.value;
            case 'Percentage':
                return buildNumFmt('percentage', { decimals });
            case 'Scientific':
                return buildNumFmt('scientific', { decimals });
            case 'Date':
            case 'Time':
                return dateTimeFmt;
            case 'Text':
                return '@';
            case 'Custom':
            default:
                return custom;
        }
    }, [category, decimals, thousands, negative, dateTimeFmt, custom, presets]);

    const valid = fa.length > 0 && isSafeNumFmt(fa);
    const preview =
        valid && coreUpdate
            ? (() => {
                  try {
                      return String(coreUpdate(fa, PREVIEW_VALUE));
                  } catch {
                      return '';
                  }
              })()
            : '';

    const handleApply = (): void => {
        if (!valid) return;
        const wb = workbookRef.current;
        if (!wb) return;
        const selection = wb.getSelection?.();
        if (!selection?.length) return;
        applyNumberFormatToSelection(
            wb,
            getInternals(wb),
            selection.map((s) => ({
                row: [s.row[0], s.row[1]] as [number, number],
                column: [s.column[0], s.column[1]] as [number, number],
            })),
            fa,
        );
        onClose();
    };

    return {
        category,
        setCategory,
        decimals,
        setDecimals,
        thousands,
        setThousands,
        negative,
        setNegative,
        dateTimeFmt,
        setDateTimeFmt,
        custom,
        setCustom,
        datePresets: DATE_PRESETS,
        timePresets: TIME_PRESETS,
        fa,
        valid,
        preview,
        handleApply,
    };
};
